import { Injectable } from '@angular/core';
import { SearchDataService } from '@core/data/api/search/search-data.service';
import { ListColumn } from '@core/models/common/list-column';
import { SearchFilterItem } from '@core/models/common/search-filter-item';
import { CreateDeliveryOrderRequest } from '@core/models/delivery/create-delivery-order-request';
import { DeliveryOrder } from '@core/models/delivery/delivery-order';
import { DeliveryOrderConfig } from '@core/models/delivery/delivery-order-config';
import { DeliveryOrderExportType } from '@core/models/delivery/delivery-order-export-type';
import { DeliveryIntegration } from '@core/models/delivery/integrations/delivery-integration';
import { DeliveryOrderQuote } from '@core/models/delivery/quote/delivery-order-quote';
import { DeliveryOrderRefundRequest } from '@core/models/delivery/refund/delivery-order-refund.request';
import { SearchDeliveryOrdersRequest } from '@core/models/delivery/search-delivery-orders-request';
import { DeliveryOrdersSummary } from '@core/models/delivery/summary/delivery-orders-summary';
import { UpdateDeliveryOrderConfigRequest } from '@core/models/delivery/update-delivery-order-config.request';
import { UpdateDeliveryOrderRequest } from '@core/models/delivery/update-delivery-order.request';
import { FileMimeType } from '@core/models/file/file-mime-type';
import { MapMarker } from '@core/models/map/map-marker';
import {
    DELIVERIES_LIST,
    DELIVERIES_LIST_ASSIGN,
    DELIVERIES_LIST_CREATE,
    DELIVERIES_LIST_CREATE_ASSIGN,
    DELIVERIES_LIST_EXPORT,
    DELIVERIES_LIST_ORDER,
    DELIVERIES_LIST_REASSIGN,
    DELIVERIES_LIST_REDISPATCH_ASSIGN,
    DELIVERIES_MAP,
    DELIVERIES_MAP_ORDER,
    DELIVERIES_MAP_REASSIGN,
    DELIVERIES_MAP_REDISPATCH_ASSIGN,
    DELIVERIES_SETTINGS,
    DELIVERIES_SETTINGS_INTEGRATIONS,
    DELIVERIES_SETTINGS_INTEGRATIONS_VIEW
} from '@core/router/app-states';
import {
    CancelDeliveryOrderAction,
    ConnectDeliveryIntegrationAction,
    CreateDeliveryOrderAction,
    DisconnectDeliveryIntegrationAction,
    DispatchDeliveryOrderAction,
    LoadDeliveryIntegrationDetailAction,
    UpdateDeliveryIntegrationSettingsAction,
    FetchDeliveryOrderConfigAction,
    FetchDeliveryOrderGroupTrackingAction,
    FetchDeliveryOrderHelpAction,
    FetchDeliveryOrderQuoteAction,
    FetchDeliveryOrderTrackingAction,
    FetchDispatchOrderAction,
    ReassignDeliveryOrderAction,
    RedispatchDeliveryOrderAction,
    RefundRequestAction,
    SearchDeliveryOrdersAction,
    SearchMoreDeliveryOrdersAction,
    SetDeliveryOrdersFilterByAction,
    SetIsDeliveryOrderOnFocusAction,
    SetNewDeliveryOrderAction,
    SetViewedDeliveryIntegrationAction,
    SetViewedDeliveryOrderAction,
    StartAddManagerNoteAction,
    StartCancelDeliveryOrderAction,
    StartRefundRequestAction,
    StartUpdateDeliveryOrderAction,
    StopDeliveryTrackingPollingAction,
    UpdateDeliveryOrderAction,
    UpdateDeliveryOrderConfigAction
} from '@core/state/deliveries/deliveries-actions';
import {
    getColumns,
    getDeliveriesGroupTracking,
    getDeliveryIntegrations,
    getDeliveryOrderConfigs,
    getDeliveryOrderIds,
    getDeliveryOrderQuotes,
    getDeliveryOrders,
    getDeliveryOrdersFilterBy,
    getDeliveryOrdersList,
    getDeliveryOrdersSummary,
    getFilterLocations,
    getLiveDeliveryOrders,
    getNewDeliveryOrder,
    getSearchQuery,
    getViewedDeliveryIntegration,
    getViewedDeliveryOrder,
    isFetching,
    isFetchingDeliveryIntegrations,
    isFetchingDeliveryOrderConfigs,
    isFetchingMore,
    isFetchingOrderQuotes,
    isFilterApplied,
    getLiveViewMapMarkers,
    getLiveEnrouteDeliveryOrders,
    getLiveDispatchingDeliveryOrders,
    getLiveRecentDeliveryOrders,
    isDeliveryOrderOnFocus
} from '@core/state/deliveries/deliveries-selectors';
import { IDeliveryOrdersFilterState } from '@core/state/deliveries/filters/deliveries-filters.state';
import { RouterGoAction } from '@core/state/router/router-actions';
import { IObservableAction } from '@core/state/shared/actions/observable-action';
import { Store } from '@ngrx/store';
import { Observable, map, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DeliveriesService {
    constructor(
        private searchDataService: SearchDataService,
        private store$: Store<any>
    ) {}

    getDeliveryOrders(): Observable<DeliveryOrder[]> {
        return this.store$.select(getDeliveryOrders);
    }

    getDeliveryOrderIds(): Observable<string[]> {
        return this.store$.select(getDeliveryOrderIds);
    }

    getColumns(): Observable<ListColumn[]> {
        return this.store$.select(getColumns);
    }

    getColumnIds(): Observable<string[]> {
        return this.getColumns().pipe(
            map((columns: ListColumn[]) => {
                return columns.map((column: ListColumn) => column.id);
            }) 
        );
    }

    getDeliveryOrdersSummary(): Observable<DeliveryOrdersSummary> {
        return this.store$.select(getDeliveryOrdersSummary);
    }

    setFilterBy(filterBy: IDeliveryOrdersFilterState) {
        this.store$.dispatch(new SetDeliveryOrdersFilterByAction(filterBy));
    }

    getFilterLocations(): Observable<SearchFilterItem[]> {
        return this.store$.select(getFilterLocations);
    }

    getFilterBy(): Observable<IDeliveryOrdersFilterState> {
        return this.store$.select(getDeliveryOrdersFilterBy);
    }

    isFilterApplied(): Observable<boolean> {
        return this.store$.select(isFilterApplied);
    }

    isFetching(): Observable<boolean> {
        return this.store$.select(isFetching);
    }

    isFetchingMore(): Observable<boolean> {
        return this.store$.select(isFetchingMore);
    }

    hasMoreDeliveryOrders(): Observable<boolean> {
        return this.store$.select(getDeliveryOrdersList)
                .pipe(
                    map((orderList) => !!(orderList.meta && orderList.meta.has_next))
                );
    }

    searchDeliveryOrders() {
        this.store$.dispatch(new SearchDeliveryOrdersAction());
    }

    searchMoreDeliveryOrders() {
        this.store$.dispatch(new SearchMoreDeliveryOrdersAction());
    }

    updateDeliveryOrder(
        orderId: string,
        request: UpdateDeliveryOrderRequest
    ): IObservableAction {
        const action$: any = new UpdateDeliveryOrderAction({
            orderId,
            request,
        });
        this.store$.dispatch(action$);
        return action$;
    }

    exportOrders(refundStatus?: string[], exportType?: DeliveryOrderExportType) {
        let request: SearchDeliveryOrdersRequest;

        this.store$.select(getSearchQuery)
            .pipe(take(1))
            .subscribe((query) => {
                request = query;

                if (refundStatus) {
                    request.filters.refund_status = refundStatus;
                }

                if (exportType) {
                    request.filters.export_type = exportType;
                }
            });

        return this.searchDataService.exportDeliveryOrders(request, FileMimeType.CSV);
    }

    viewMap() {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_MAP,
            })
        );
    }

    viewMapDeliveryOrder(orderId: string) {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_MAP_ORDER,
                params: { orderId },
            })
        );
    }

    fetchDeliveryOrderTracking(orderId: string) {
        this.store$.dispatch(new FetchDeliveryOrderTrackingAction({ orderId }));
    }

    stopDeliveryOrderTrackingPolling() {
        this.store$.dispatch(new StopDeliveryTrackingPollingAction());
    }

    fetchDeliveryOrderGroupTracking(locationId: string) {
        this.store$.dispatch(new FetchDeliveryOrderGroupTrackingAction({ locationId }));
    }

    getDeliveryOrderGroupTracking() {
        return this.store$.select(getDeliveriesGroupTracking);
    }

    fetchDispatchOrder(internalId: string): IObservableAction {
        const action: any = new FetchDispatchOrderAction({
            orderId: internalId,
        });
        this.store$.dispatch(action);
        return action;
    }

    isFetchingDeliveryOrderQuotes(): Observable<boolean> {
        return this.store$.select(isFetchingOrderQuotes);
    }

    getViewedDeliveryOrder(): Observable<DeliveryOrder> {
        return this.store$.select(getViewedDeliveryOrder);
    }

    getNewDeliveryOrder(): Observable<CreateDeliveryOrderRequest> {
        return this.store$.select(getNewDeliveryOrder);
    }

    setNewDeliveryOrder(order: CreateDeliveryOrderRequest) {
        this.store$.dispatch(new SetNewDeliveryOrderAction({ order }));
    }

    getDeliveryOrderQuotes(): Observable<{ [id: string]: DeliveryOrderQuote[] }> {
        return this.store$.select(getDeliveryOrderQuotes);
    }

    getAllDeliveriesConfigurations(): Observable<{ [id: string]: DeliveryOrderConfig }> {
        return this.store$.select(getDeliveryOrderConfigs);
    }

    getDeliveriesConfigurationPerLocation(locationId: string): Observable<DeliveryOrderConfig> {
        return this.getAllDeliveriesConfigurations()
                .pipe(map((orderConfigs) => orderConfigs[locationId]));
    }

    isFetchingDeliveryOrderConfig(): Observable<boolean> {
        return this.store$.select(isFetchingDeliveryOrderConfigs);
    }

    viewDeliveryOrder(orderId: string) {
        this.store$.dispatch(new RouterGoAction({
            name: DELIVERIES_LIST_ORDER,
            params: { orderId },
        }));
    }

    startDeliveryOrderCreate() {
        this.store$.dispatch(new RouterGoAction({
            name: DELIVERIES_LIST_CREATE
        }));
    }

    startExportDeliveryOrders() {
        this.store$.dispatch(new RouterGoAction({
            name: DELIVERIES_LIST_EXPORT
        }));
    }

    reassignDeliveryOrder(orderId: string, id: string, driverId: string, autoAssign: boolean): IObservableAction {
        const action$: any = new ReassignDeliveryOrderAction({
            orderId,
            driverId,
            autoAssign,
            id
        });

        this.store$.dispatch(action$);
        return action$; 
    }

    dispatchDeliveryOrder(order: DeliveryOrder): IObservableAction {
        const action$: any = new DispatchDeliveryOrderAction({
            orderId: order.id,
            order,
        });

        this.store$.dispatch(action$);
        return action$;
    }

    redispatchDeliveryOrder(order: DeliveryOrder): IObservableAction {
        const action$: any = new RedispatchDeliveryOrderAction({
            orderId: order.id,
            order,
        });

        this.store$.dispatch(action$);
        return action$;
    }

    createDeliveryOrder(request: CreateDeliveryOrderRequest): IObservableAction {
        const action$: any = new CreateDeliveryOrderAction({
            request,
        });
        this.store$.dispatch(action$);
        return action$;
    }

    viewDeliveryOrders() {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_LIST
            })
        );
    }

    cancelDeliveryOrder(orderId: string, isRedispatch?: boolean): IObservableAction {
        const action$: any = new CancelDeliveryOrderAction({
            orderId,
            isRedispatch,
        });
        this.store$.dispatch(action$);
        return action$;
    }

    fetchDeliveryOrderQuote(pickupRequiredAt: string, deliveryTimeUtc: string) {
        this.store$.dispatch(
            new FetchDeliveryOrderQuoteAction({
                pickupRequiredAt,
                deliveryTimeUtc,
            })
        );
    }

    assignNewDeliveryOrder() {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_LIST_CREATE_ASSIGN,
            })
        );
    }

    fetchSettings(locationId: string) {
        this.store$.dispatch(new FetchDeliveryOrderConfigAction({ locationId }));
    }

    assignRedispatchedDeliveryOrder() {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_LIST_REDISPATCH_ASSIGN,
            })
        );
    }

    assignMapRedispatchedDeliveryOrder() {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_MAP_REDISPATCH_ASSIGN,
            })
        );
    }

    requestRefund(order: DeliveryOrder, request: DeliveryOrderRefundRequest): IObservableAction {
        const action$: any = new RefundRequestAction({
            order,
            request,
        });
        this.store$.dispatch(action$);
        return action$;
    }

    startReassignDeliveryOrder(order: DeliveryOrder) {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_LIST_REASSIGN,
                params: {
                    orderId: order.id,
                    order,
                },
            })
        );
    }

    startReassignMapDeliveryOrder(order: DeliveryOrder) {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_MAP_REASSIGN,
                params: {
                    orderId: order.id,
                    order,
                },
            })
        );
    }

    startRefundRequest(order: DeliveryOrder): IObservableAction {
        const action$: any = new StartRefundRequestAction({
            order,
        });
        this.store$.dispatch(action$);
        return action$;
    }

    refundRequest(order: DeliveryOrder, request: DeliveryOrderRefundRequest): IObservableAction {
        const action$: any = new RefundRequestAction({
            order,
            request,
        });

        this.store$.dispatch(action$);
        return action$;
    }

    startAddManagerNote(order: DeliveryOrder): IObservableAction {
        const action$: any = new StartAddManagerNoteAction({
            order,
        });

        this.store$.dispatch(action$);
        return action$;
    }

    startCancelDeliveryOrder(order: DeliveryOrder) {
        this.store$.dispatch(
            new StartCancelDeliveryOrderAction({
                order,
            })
        );
    }

    startUpdateDeliveryOrder(order: DeliveryOrder) {
        this.store$.dispatch(
            new StartUpdateDeliveryOrderAction({
                order,
            })
        );
    }

    startAssignDeliveryOrder(orderId: string = null) {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_LIST_ASSIGN,
                params: { orderId },
            })
        );
    }

    fetchDeliveryOrderHelp(orderId: string): IObservableAction {
        const action: any = new FetchDeliveryOrderHelpAction({ orderId });
        this.store$.dispatch(action);
        return action;
    }

    startSettingsEdit(setting: string, locationId: string) {
        this.store$.dispatch(
            new RouterGoAction({
                name: `${ DELIVERIES_SETTINGS }.${setting}`,
                params: {
                    locationId,
                },
            })
        );
    }

    viewDeliveriesSettings(locationId?: string) {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_SETTINGS,
                params: {
                    locationId
                },
            })
        );
    }

    updatePrepTime(locationId: string, prepTime: number) {
        let request: UpdateDeliveryOrderConfigRequest = new UpdateDeliveryOrderConfigRequest(); 
        request.pickupPrepTime = prepTime;

        this.store$.dispatch(
            new UpdateDeliveryOrderConfigAction({
                locationId,
                request,
            })
        );
    }

    updateDeliveriesConfig(
        locationId: string,
        request: UpdateDeliveryOrderConfigRequest
    ): IObservableAction {
        const action$: any = new UpdateDeliveryOrderConfigAction({
            locationId,
            request,
        });

        this.store$.dispatch(action$);
        return action$;
    }

    getDeliveryIntegrations(): Observable<DeliveryIntegration[]> {
        return this.store$.select(getDeliveryIntegrations);
    }

    isFetchingDeliveryIntegrations(): Observable<boolean> {
        return this.store$.select(isFetchingDeliveryIntegrations);
    }

    setViewedDeliveryIntegration(provider: string) {
        this.store$.dispatch(new SetViewedDeliveryIntegrationAction({ provider }))
    }

    viewDeliveryIntegrations() {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_SETTINGS_INTEGRATIONS
            })
        );
    }

    viewedDeliveryIntegration(provider: string) {
        this.store$.dispatch(
            new RouterGoAction({
                name: DELIVERIES_SETTINGS_INTEGRATIONS_VIEW,
                params: {
                    provider
                }
            })
        );
    }

    getViewedDeliveryIntegration(): Observable<DeliveryIntegration> {
        return this.store$.select(getViewedDeliveryIntegration);
    }

    connectDeliveryIntegration(
        payload: string | { provider: string; apiKey?: string }
    ): IObservableAction {
        // Backwards-compatible: the oauth flow was passing a bare
        // provider string.
        const normalised =
            typeof payload === 'string' ? { provider: payload } : payload;
        const action$: any = new ConnectDeliveryIntegrationAction(normalised);
        this.store$.dispatch(action$);
        return action$;
    }

    disconnectDeliveryIntegration(id: string): IObservableAction {
        const action$: any = new DisconnectDeliveryIntegrationAction({
            id
        });
        this.store$.dispatch(action$);
        return action$;
    }

    loadDeliveryIntegrationDetail(id: string): IObservableAction {
        const action$: any = new LoadDeliveryIntegrationDetailAction({ id });
        this.store$.dispatch(action$);
        return action$;
    }

    updateDeliveryIntegrationSettings(
        id: string,
        settings: Record<string, any>
    ): IObservableAction {
        const action$: any = new UpdateDeliveryIntegrationSettingsAction({ id, settings });
        this.store$.dispatch(action$);
        return action$;
    }

    getLiveDeliveryOrders(): Observable<DeliveryOrder[]> {
        return this.store$.select(getLiveDeliveryOrders);
    }

    getLiveViewMarkers(): Observable<MapMarker[]> {
        return this.store$.select(getLiveViewMapMarkers);
    }

    getLiveEnrouteDeliveryOrders(): Observable<DeliveryOrder[]> {
        return this.store$.select(getLiveEnrouteDeliveryOrders);
    }

    getLiveDispatchingDeliveryOrders(): Observable<DeliveryOrder[]> {
        return this.store$.select(getLiveDispatchingDeliveryOrders);
    }

    getLiveRecentDeliveryOrders(): Observable<DeliveryOrder[]> {
        return this.store$.select(getLiveRecentDeliveryOrders);
    }

    isDeliveryOrderOnFocus(): Observable<boolean> {
        return this.store$.select(isDeliveryOrderOnFocus);
    }

    setIsDeliveryOrderOnFocus(value: boolean) {
        this.store$.dispatch(new SetIsDeliveryOrderOnFocusAction({ value }));
    }

    setViewedDeliveryOrderAction(orderId: string) {
        this.store$.dispatch(new SetViewedDeliveryOrderAction({ orderId }));
    }
}
